/**
 * palette — the sanctioned semantic color palette for maps and charts.
 *
 * Fluent `tokens.*` cover component styling, but two rendering contexts
 * cannot consume CSS variables and had every app inventing its own hex:
 *
 *   1. Canvas / WebGL layers (MapLibre paint expressions, Leaflet divIcon
 *      HTML strings, marker cluster bubbles) — resolved at draw time, no
 *      CSS cascade available.
 *   2. Chart series that need STABLE semantic colors across light + dark
 *      (an "offline" red must read as the same alarm red on a NOC wall in
 *      dark mode and a daylight laptop).
 *
 * Two tiers, pick by context:
 *
 *   - `statusColor` / `severityColor` / `chartSeries` — **theme-static hex**.
 *     Use ONLY where CSS variables physically cannot reach (canvas, WebGL,
 *     `divIcon` HTML strings) or where a semantic data color must not flip
 *     with the theme. Mid-tone values, hand-checked for ≥3:1 contrast
 *     against both `colorNeutralBackground1` variants (WCAG 1.4.11
 *     non-text). These are the long-standing de-facto fleet map colors —
 *     canonised here so apps stop redeclaring them.
 *
 *   - `statusToken` / `severityToken` — **token-derived** (CSS variable
 *     strings). Use for SVG/DOM-rendered visuals (Recharts fills, legend
 *     dots, sparkline strokes) so they keep adapting to the active theme.
 *
 * Hard rule unchanged: app code never writes raw hex. If a color is
 * missing here, extend THIS module — don't inline a literal.
 */
import { tokens } from '@fluentui/react-components';

/* ──────────────────────────────────────────────────────────────────────
 * Station / connectivity status
 * ────────────────────────────────────────────────────────────────────── */

export type StationStatusColor =
  | 'healthy'    /* online + idle */
  | 'charging'   /* session in progress */
  | 'offline'    /* broker lost the station */
  | 'degraded'   /* partial fault / SLA at risk */
  | 'inactive'   /* decommissioned / is_active=false */
  | 'unknown';   /* no data — data-quality fix needed */

/** Theme-static hex for canvas/WebGL map layers and divIcon HTML. */
export const statusColor: Record<StationStatusColor, string> = {
  healthy:  '#10b981',
  charging: '#3b82f6',
  offline:  '#ef4444',
  degraded: '#f59e0b',
  inactive: '#6b7280',
  unknown:  '#9ca3af',
};

/** Token-derived equivalents for SVG/DOM visuals — theme-adaptive. */
export const statusToken: Record<StationStatusColor, string> = {
  healthy:  tokens.colorPaletteGreenForeground1,
  charging: tokens.colorPaletteBlueForeground2,
  offline:  tokens.colorPaletteRedForeground1,
  degraded: tokens.colorPaletteDarkOrangeForeground1,
  inactive: tokens.colorNeutralForeground3,
  unknown:  tokens.colorNeutralForeground4,
};

/* ──────────────────────────────────────────────────────────────────────
 * Incident severity
 * ────────────────────────────────────────────────────────────────────── */

export type SeverityColor = 'critical' | 'high' | 'medium' | 'low';

/** Theme-static hex (canvas/WebGL). Matches the P1→P4 ladder. */
export const severityColor: Record<SeverityColor, string> = {
  critical: '#dc2626',
  high:     '#f59e0b',
  medium:   '#3b82f6',
  low:      '#6b7280',
};

/** Token-derived equivalents (SVG/DOM). */
export const severityToken: Record<SeverityColor, string> = {
  critical: tokens.colorPaletteRedForeground1,
  high:     tokens.colorPaletteDarkOrangeForeground1,
  medium:   tokens.colorPaletteBlueForeground2,
  low:      tokens.colorNeutralForeground3,
};

/* ──────────────────────────────────────────────────────────────────────
 * Categorical chart series
 * ────────────────────────────────────────────────────────────────────── */

/**
 * Eight-step categorical ramp for multi-series charts (energy by Werk,
 * incidents by category, …). Order is fixed — series N always gets
 * `chartSeries[N % chartSeries.length]` so colors stay stable as data
 * grows. Hues are spaced for distinguishability, including for the two
 * most common color-vision deficiencies (no red/green adjacency).
 */
export const chartSeries: readonly string[] = [
  '#2563eb', /* blue   */
  '#10b981', /* green  */
  '#f59e0b', /* amber  */
  '#8b5cf6', /* violet */
  '#0891b2', /* cyan   */
  '#db2777', /* pink   */
  '#65a30d', /* lime   */
  '#64748b', /* slate  */
];

/** Stable series color by index (wraps around the ramp). */
export function chartSeriesColor(index: number): string {
  return chartSeries[((index % chartSeries.length) + chartSeries.length) % chartSeries.length];
}

/* ──────────────────────────────────────────────────────────────────────
 * Overview time charts, light and dark
 * ────────────────────────────────────────────────────────────────────── */

/**
 * The EAS Overview charts, validated rather than chosen by eye.
 *
 * Checked with the dataviz method's palette validator on 2026-09-18, against
 * the card surface each theme actually renders on (white, and the Fluent dark
 * card, #292929):
 *
 *   outcomes      blue, yellow, red     adjacent CVD 19.8 light, 10.2 dark
 *   settlements   + violet              adjacent CVD 19.8 light, 10.2 dark
 *   latency       one blue, ordinal     monotone lightness, one hue, both modes
 *   lanes         the first five slots  adjacent CVD 9.1 light, 8.4 dark
 *
 * Yellow sits under 3:1 on white, which the method allows only with labels or
 * a table beside it; every chart has a legend, a readout and a table view.
 * `unaskable` is a deliberate grey and fails the chroma check by design: it is
 * the context half of an emphasis chart, not a series to be told apart.
 *
 * Dark is its own set of steps, not an automatic flip: the ordinal ramp runs
 * the other way on a dark surface, where more is lighter.
 */
export interface OverviewChartColours {
  approved: string;
  unknown: string;
  blocked: string;
  failed: string;
  p50: string;
  p95: string;
  p99: string;
  lookable: string;
  unaskable: string;
  refused: string;
  lanes: readonly string[];
}

export const overviewChartColours: Record<'light' | 'dark', OverviewChartColours> = {
  light: {
    approved: '#2a78d6',
    unknown: '#eda100',
    blocked: '#d03b3b',
    failed: '#4a3aa7',
    p50: '#86b6ef',
    p95: '#2a78d6',
    p99: '#104281',
    lookable: '#4a3aa7',
    unaskable: '#a8a69f',
    refused: '#d03b3b',
    lanes: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4'],
  },
  dark: {
    approved: '#3987e5',
    unknown: '#c98500',
    blocked: '#d03b3b',
    failed: '#9085e9',
    p50: '#256abf',
    p95: '#5598e7',
    p99: '#b7d3f6',
    lookable: '#9085e9',
    unaskable: '#6b6a65',
    refused: '#e66767',
    lanes: ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181'],
  },
};
